import { createTheme } from '@mui/material/styles';

/**
 * memo-react 主题颜色配置 —— 与 memo-flutter 的 cc_design 主题体系对齐。
 *
 * 对齐基准（memo-flutter 侧）：
 * - assets/themes/cc-light.json / cc-dark.json（应用品牌色 override）
 * - cc-lib/dart/cc_design CcColors.light / CcColors.dark（未 override 字段的回退值）
 * - CcTheme._buildTheme（组件级配色：hoverColor、ListTile selected、Input、Chip、
 *   AppBar、浮动层背景与阴影等）
 *
 * 即 memo-flutter 的实际生效色 = cc_design base ⊕ 应用 override ⊕ CcColors 回退，
 * 本文件按同一结果逐项对齐，保证两个端外观一致。
 *
 * 透明度令牌（cc_design CcOpacity）：
 * - focus/hover(交互叠加) 0.08 / 0.16、nav active 背景 0.16、disabled 0.50
 */

// ─────────────────────────────────────────────────────────────────
// cc_design 语义色令牌（与 memo-flutter 逐项对应）
// ─────────────────────────────────────────────────────────────────

export interface CcColorTokens {
  // Brand
  primary: string;
  onPrimary: string;
  primaryContainer: string;
  onPrimaryContainer: string;
  secondary: string;
  onSecondary: string;
  secondaryContainer: string;
  onSecondaryContainer: string;
  // Surface
  background: string;
  onBackground: string;
  surface: string;
  onSurface: string;
  surfaceVariant: string;
  onSurfaceVariant: string;
  surfaceContainerLowest: string;
  surfaceContainerLow: string;
  surfaceContainer: string;
  surfaceContainerHigh: string;
  surfaceContainerHighest: string;
  // Outline / Inverse
  outline: string;
  outlineVariant: string;
  inverseSurface: string;
  inverseOnSurface: string;
  inversePrimary: string;
  // Semantic
  success: string;
  onSuccess: string;
  warning: string;
  onWarning: string;
  error: string;
  onError: string;
  errorContainer: string;
  onErrorContainer: string;
  info: string;
  onInfo: string;
  scrim: string;
  // Input / Misc
  inputBackground: string;
  inputBorder: string;
  inputHint: string;
  inputFill: string;
  icon: string;
  divider: string;
  shadow: string;
  // IM / 导航
  bubbleSelf: string;
  bubbleOther: string;
  appBarBackground: string;
  appBarForeground: string;
  navBarBackground: string;
  sectionHeaderBackground: string;
  sectionHeaderText: string;
  unreadBadge: string;
  unreadBadgeText: string;
  groupAccent: string;
  roleOwner: string;
  roleAdmin: string;
  onlineIndicator: string;
  // 交互叠加（cc_design CcOpacity → primary alpha）
  primaryAlpha8: string;   // focus / 全局 hover 叠加
  primaryAlpha16: string;  // hover 叠加 / nav active 背景
}

/** 浅色令牌 —— cc-light.json ⊕ CcColors.light 回退 */
export const CcLightColors: CcColorTokens = {
  primary: '#E64A1A',
  onPrimary: '#FFFFFF',
  primaryContainer: '#FFE0D0',
  onPrimaryContainer: '#7A280C',
  secondary: '#16A34A',
  onSecondary: '#FFFFFF',
  secondaryContainer: '#DCFCE7',
  onSecondaryContainer: '#14532D',
  background: '#FFFFFF',
  onBackground: '#000000',
  surface: '#FFFFFF',
  onSurface: '#000000',
  surfaceVariant: '#F2F2F2',
  onSurfaceVariant: '#808080',
  surfaceContainerLowest: '#FFFFFF',
  surfaceContainerLow: '#F7F2FA',
  surfaceContainer: '#F3EDF7',
  surfaceContainerHigh: '#ECE6F0',
  surfaceContainerHighest: '#E6E0E9',
  outline: '#E0E0E0',
  outlineVariant: '#C4C4C4',
  inverseSurface: '#322F35',
  inverseOnSurface: '#F5EFF7',
  inversePrimary: '#D0BCFF',
  success: '#16A34A',
  onSuccess: '#FFFFFF',
  warning: '#D97706',
  onWarning: '#FFFFFF',
  error: '#DC2626',
  onError: '#FFFFFF',
  errorContainer: '#F9DEDC',
  onErrorContainer: '#410E0B',
  info: '#2563EB',
  onInfo: '#FFFFFF',
  scrim: '#000000',
  inputBackground: '#FFFFFF',
  inputBorder: '#E0E0E0',
  inputHint: '#808080',
  inputFill: '#F2F2F2',
  icon: '#5C5C5C',
  divider: '#E0E0E0',
  shadow: 'rgba(0, 0, 0, 0.10)',
  bubbleSelf: '#E64A1A',
  bubbleOther: '#F2F2F2',
  appBarBackground: '#FFFFFF',
  appBarForeground: '#E64A1A',
  navBarBackground: '#FFFFFF',
  sectionHeaderBackground: '#F2F2F2',
  sectionHeaderText: '#E64A1A',
  unreadBadge: '#E64A1A',
  unreadBadgeText: '#FFFFFF',
  groupAccent: '#16A34A',
  roleOwner: '#D97706',
  roleAdmin: '#2563EB',
  onlineIndicator: '#16A34A',
  primaryAlpha8: 'rgba(230, 74, 26, 0.08)',
  primaryAlpha16: 'rgba(230, 74, 26, 0.16)'
};

/** 深色令牌 —— cc-dark.json ⊕ CcColors.dark 回退 */
export const CcDarkColors: CcColorTokens = {
  primary: '#FF6B3D',
  onPrimary: '#FFFFFF',
  primaryContainer: '#993210',
  onPrimaryContainer: '#FFE0D0',
  secondary: '#22C55E',
  onSecondary: '#FFFFFF',
  secondaryContainer: '#166534',
  onSecondaryContainer: '#DCFCE7',
  background: '#000000',
  onBackground: '#FFFFFF',
  surface: '#1A1A1A',
  onSurface: '#FFFFFF',
  surfaceVariant: '#333333',
  onSurfaceVariant: '#ADADAD',
  surfaceContainerLowest: '#0F0D13',
  surfaceContainerLow: '#1D1B20',
  surfaceContainer: '#211F26',
  surfaceContainerHigh: '#2B2930',
  surfaceContainerHighest: '#36343B',
  outline: '#5C5C5C',
  outlineVariant: '#333333',
  inverseSurface: '#E6E0E9',
  inverseOnSurface: '#322F35',
  inversePrimary: '#6750A4',
  success: '#22C55E',
  onSuccess: '#FFFFFF',
  warning: '#F59E0B',
  onWarning: '#FFFFFF',
  error: '#EF4444',
  onError: '#FFFFFF',
  errorContainer: '#8C1D18',
  onErrorContainer: '#F9DEDC',
  info: '#3B82F6',
  onInfo: '#FFFFFF',
  scrim: '#000000',
  inputBackground: '#1A1A1A',
  inputBorder: '#5C5C5C',
  inputHint: '#ADADAD',
  inputFill: '#333333',
  icon: '#ADADAD',
  divider: '#5C5C5C',
  shadow: 'rgba(0, 0, 0, 0.20)',
  bubbleSelf: '#FF6B3D',
  bubbleOther: '#333333',
  appBarBackground: '#1A1A1A',
  appBarForeground: '#FF6B3D',
  navBarBackground: '#1A1A1A',
  sectionHeaderBackground: '#333333',
  sectionHeaderText: '#FF6B3D',
  unreadBadge: '#FF6B3D',
  unreadBadgeText: '#FFFFFF',
  groupAccent: '#22C55E',
  roleOwner: '#F59E0B',
  roleAdmin: '#3B82F6',
  onlineIndicator: '#22C55E',
  primaryAlpha8: 'rgba(255, 107, 61, 0.08)',
  primaryAlpha16: 'rgba(255, 107, 61, 0.16)'
};

/** 按模式取 cc_design 语义令牌 */
export function getCcColors(mode: 'light' | 'dark'): CcColorTokens {
  return mode === 'dark' ? CcDarkColors : CcLightColors;
}

// ─────────────────────────────────────────────────────────────────
// MUI palette 语义对象（保持既有导出形状，值全部来自 cc 令牌）
// ─────────────────────────────────────────────────────────────────

export const ThemeColors = {
  // 主色调 - 橙色主题（flutter primary / primaryContainer / onPrimaryContainer）
  primary: {
    light: CcLightColors.primaryContainer,
    main: CcLightColors.primary,
    dark: CcDarkColors.primaryContainer, // 深色调（按压/hover 加深），取 cc-dark primaryContainer
    contrastText: CcLightColors.onPrimary
  },

  // 辅助色调 - 绿色点缀色（flutter secondary / secondaryContainer / onSecondaryContainer）
  // 仅作为极少量的点缀色使用，用于平衡主色调的橙色
  // 不应使用在大面积的界面中，如菜单文字状态、背景颜色等
  secondary: {
    light: CcLightColors.secondaryContainer,
    main: CcLightColors.secondary,
    dark: CcLightColors.onSecondaryContainer,
    contrastText: CcLightColors.onSecondary
  },

  // 成功状态 - 绿色（flutter success == secondary 同值）
  success: {
    light: CcLightColors.secondaryContainer,
    main: CcLightColors.success,
    dark: CcLightColors.onSecondaryContainer,
    contrastText: CcLightColors.onSuccess
  },

  // 警告状态 - 橙黄（flutter warning）
  warning: {
    light: '#FFF7E6',
    main: CcLightColors.warning,
    dark: '#92400E',
    contrastText: CcLightColors.onWarning
  },

  // 错误状态 - 红色（flutter error）
  error: {
    light: CcLightColors.errorContainer,
    main: CcLightColors.error,
    dark: '#991B1B',
    contrastText: CcLightColors.onError
  },

  // 信息状态 - 蓝色（flutter info）
  info: {
    light: '#EFF6FF',
    main: CcLightColors.info,
    dark: '#1E40AF',
    contrastText: CcLightColors.onInfo
  },

  // 中性色 - 浅灰色（值取自 cc 设计系统中性面：surfaceVariant/surfaceContainer*/outline 系）
  // MUI 内置 grey 调色板在 sx 中使用: grey.50 / grey.100 / grey.200 / grey.300 / grey.400
  neutral: {
    50: '#FAFAFA',
    100: CcLightColors.surfaceVariant,      // #F2F2F2
    200: CcLightColors.surfaceContainerHighest, // #E6E0E9
    300: CcLightColors.outlineVariant,      // #C4C4C4
    400: CcDarkColors.onSurfaceVariant,     // #ADADAD
    500: CcLightColors.onSurfaceVariant,    // #808080
    600: CcLightColors.icon,                // #5C5C5C
    700: '#424242',
    800: CcDarkColors.surfaceVariant,       // #333333
    900: CcDarkColors.surface               // #1A1A1A
  },

  // 文本颜色（flutter onSurface / onSurfaceVariant / M3 disabled-content 38%）
  text: {
    primary: CcLightColors.onSurface,       // #000000
    secondary: CcLightColors.onSurfaceVariant, // #808080
    disabled: 'rgba(0, 0, 0, 0.38)'
  },

  // 背景颜色（flutter background / surface）
  background: {
    default: CcLightColors.background,      // #FFFFFF
    paper: CcLightColors.surface            // #FFFFFF
  },

  // 边框和分割线（flutter outline / divider 同值）
  divider: CcLightColors.outline
} as const;

/**
 * 深色模式语义对象（04-1.1）—— 值全部来自 CcDarkColors（cc-dark.json）。
 * 品牌色使用 cc-dark 的亮橙/亮绿变体，与 memo-flutter 深色模式一致。
 */
export const DarkThemeColors = {
  text: {
    primary: CcDarkColors.onSurface,        // #FFFFFF
    secondary: CcDarkColors.onSurfaceVariant, // #ADADAD
    disabled: 'rgba(255, 255, 255, 0.38)'
  },
  background: {
    default: CcDarkColors.background,       // #000000（flutter dark background）
    paper: CcDarkColors.surface             // #1A1A1A（flutter dark surface）
  },
  divider: CcDarkColors.divider             // #5C5C5C（flutter dark divider）
} as const;

// 深色模式下覆盖 MUI 内置 grey 调色板（语义反转：编号越小越接近背景）。
// 锚点取自 flutter 深色表面：background #000 / surface #1A1A1A /
// surfaceVariant #333333 / outline & divider #5C5C5C / onSurfaceVariant #ADADAD
const DarkGreyPalette = {
  50: '#0A0A0A',
  100: CcDarkColors.surface,              // #1A1A1A
  200: '#262626',
  300: CcDarkColors.surfaceVariant,       // #333333
  400: CcDarkColors.outline,              // #5C5C5C
  500: '#808080',
  600: CcDarkColors.onSurfaceVariant,     // #ADADAD
  700: CcLightColors.outlineVariant,      // #C4C4C4
  800: CcLightColors.surfaceContainerHighest, // #E6E0E9
  900: '#F5F5F5'
};

/**
 * 组件样式配置
 */
// 基础样式常量 (space-2 / space-3 / space-4, radius-sm / radius-md / radius-lg)
const BaseStyles = {
  spacing: {
    small: '8px',
    medium: '12px',
    large: '16px'
  },
  borderRadius: {
    small: '8px',
    medium: '12px',
    large: '16px'
  }
} as const;

// 为兼容性添加默认borderRadius值 (radius-sm)
const DefaultBorderRadius = '8px';
// 头像和图标专用的更小圆角值 (radius-avatar)
const AvatarBorderRadius = '8px';

// 交互状态样式（flutter nav 选中：文字 primary、背景 primaryContainer）
const StateStyles = {
  hover: {
    primary: {
      backgroundColor: ThemeColors.primary.light,
      color: ThemeColors.primary.main,
      fontWeight: 'bolder'
    },
    selected: {
      backgroundColor: ThemeColors.primary.light,
      color: ThemeColors.primary.main,
      fontWeight: 'bolder'
    }
  }
} as const;

export const ComponentStyles = {
  // 基础样式
  base: {
    ...BaseStyles,
    borderRadius: {
      ...BaseStyles.borderRadius,
      default: DefaultBorderRadius,
      avatar: AvatarBorderRadius
    }
  },

  // 交互状态样式
  states: StateStyles,

  // 按钮样式（flutter ElevatedButton: primary 底 / onPrimary 字；hover 为 cc 设计深调）
  button: {
    primary: {
      backgroundColor: ThemeColors.primary.main,
      color: ThemeColors.primary.contrastText,
      borderRadius: BaseStyles.borderRadius.small,
      '&:hover': {
        backgroundColor: ThemeColors.primary.dark
      }
    },
    secondary: {
      backgroundColor: ThemeColors.secondary.main,
      color: ThemeColors.secondary.contrastText,
      borderRadius: BaseStyles.borderRadius.small,
      '&:hover': {
        backgroundColor: ThemeColors.secondary.dark
      }
    },
    outlined: {
      border: `1px solid ${ThemeColors.primary.main}`,
      color: ThemeColors.primary.main,
      backgroundColor: 'transparent',
      borderRadius: BaseStyles.borderRadius.small,
      '&:hover': {
        backgroundColor: ThemeColors.primary.light
      }
    }
  },

  // 图标样式（flutter IconTheme: onSurface @ 60%；语义图标用语义色）
  icon: {
    primary: {
      color: ThemeColors.primary.main
    },
    success: {
      color: ThemeColors.success.main
    },
    warning: {
      color: ThemeColors.warning.main
    },
    error: {
      color: ThemeColors.error.main
    },
    info: {
      color: ThemeColors.info.main
    }
  },

  // 文字样式
  typography: {
    primary: {
      color: ThemeColors.primary.main
    },
    secondary: {
      color: ThemeColors.text.secondary
    },
    success: {
      color: ThemeColors.success.main
    },
    warning: {
      color: ThemeColors.warning.main
    },
    error: {
      color: ThemeColors.error.main
    },
    link: {
      color: ThemeColors.primary.main,
      cursor: 'pointer',
      textDecoration: 'underline',
      '&:hover': {
        color: ThemeColors.primary.dark
      }
    }
  },

  // 表单控件样式（flutter InputDecoration: 描边 outlineVariant，hover/focus primary）
  form: {
    input: {
      borderRadius: BaseStyles.borderRadius.small,
      backgroundColor: 'transparent',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: CcLightColors.outlineVariant
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: ThemeColors.primary.main
      }
    },
    textField: {
      borderRadius: BaseStyles.borderRadius.small,
      backgroundColor: 'transparent'
    },
    select: {
      borderRadius: BaseStyles.borderRadius.small
    }
  },

  // 列表项样式（flutter ListTile: selectedTileColor = primary @ 8%）
  list: {
    item: {
      // 移除圆角设置，保持直角
      '&.Mui-selected': StateStyles.hover.selected,
      '&:hover': StateStyles.hover.primary
    },
    itemButton: {
      padding: BaseStyles.spacing.small,
      // 移除圆角设置，保持直角
      '&.Mui-selected': StateStyles.hover.selected,
      '&:hover': StateStyles.hover.primary,
      selected: StateStyles.hover.selected,
      hover: StateStyles.hover.primary
    },
    itemAvatar: {
      minWidth: 'auto',
      marginRight: BaseStyles.spacing.small
    }
  },

  // 容器样式 (shadow-1)
  container: {
    paper: {
      backgroundColor: ThemeColors.background.paper,
      borderRadius: BaseStyles.borderRadius.medium,
      boxShadow: '0 1px 2px rgba(10, 15, 26, 0.06)'
    },
    card: {
      backgroundColor: ThemeColors.background.paper,
      borderRadius: BaseStyles.borderRadius.medium,
      padding: BaseStyles.spacing.medium,
      boxShadow: '0 1px 2px rgba(10, 15, 26, 0.06)'
    }
  }
} as const;

/**
 * 认证相关组件样式
 */
export const AuthComponentStyles = {
  // 认证错误页面
  authenticationError: {
    icon: ComponentStyles.icon.error,
    title: ComponentStyles.typography.error,
    button: ComponentStyles.button.primary,
    retryButton: ComponentStyles.button.primary,
    homeButton: ComponentStyles.button.secondary
  },

  // 会话丢失组件
  sessionLost: {
    icon: ComponentStyles.icon.warning,
    title: ComponentStyles.typography.warning,
    button: ComponentStyles.button.primary
  },

  // 认证成功页面
  authenticationSuccess: {
    icon: ComponentStyles.icon.success,
    title: ComponentStyles.typography.success,
    message: ComponentStyles.typography.secondary,
    link: ComponentStyles.typography.link
  },

  // 认证中组件
  authenticating: {
    icon: ComponentStyles.icon.primary,
    title: ComponentStyles.typography.primary,
    message: ComponentStyles.typography.secondary
  },

  // 加载组件
  loading: {
    icon: ComponentStyles.icon.primary,
    title: ComponentStyles.typography.primary,
    message: ComponentStyles.typography.secondary
  }
} as const;

/**
 * Material-UI主题配置：按 colorMode 构建（04-1.1 深色模式落地）。
 *
 * 全部色值取自 getCcColors(mode)（与 memo-flutter cc-light/cc-dark 一致）：
 * 深色模式下品牌色切换为亮橙 #FF6B3D / 亮绿 #22C55E，背景 #000 / 表面 #1A1A1A，
 * 分割线 #5C5C5C，次级文本 #ADADAD —— 与 memo-flutter 深色主题逐项相同。
 */
export function createAppTheme(mode: 'light' | 'dark' = 'light') {
  const isDark = mode === 'dark';
  const cc = getCcColors(mode);
  const appText = isDark ? DarkThemeColors.text : ThemeColors.text;

  // 浮动层（弹窗/菜单）：浅色=纯白，深色=表面色；阴影按明暗分别取值
  // （对齐 cc_design CcTheme._buildTheme 的 floatingBackground / floatingShadow）
  const floatingShadow = isDark
    ? '0 8px 28px rgba(0, 0, 0, 0.70)'
    : '0 8px 28px rgba(10, 15, 26, 0.25)';

  return createTheme({
  palette: {
    mode,
    primary: {
      light: cc.primaryContainer,
      main: cc.primary,
      dark: CcDarkColors.primaryContainer,
      contrastText: cc.onPrimary
    },
    secondary: {
      light: cc.secondaryContainer,
      main: cc.secondary,
      dark: cc.onSecondaryContainer,
      contrastText: cc.onSecondary
    },
    success: {
      light: cc.secondaryContainer,
      main: cc.success,
      dark: cc.onSecondaryContainer,
      contrastText: cc.onSuccess
    },
    warning: {
      light: '#FFF7E6',
      main: cc.warning,
      dark: '#92400E',
      contrastText: cc.onWarning
    },
    error: {
      light: cc.errorContainer,
      main: cc.error,
      dark: '#991B1B',
      contrastText: cc.onError
    },
    info: {
      light: '#EFF6FF',
      main: cc.info,
      dark: '#1E40AF',
      contrastText: cc.onInfo
    },
    text: {
      primary: appText.primary,
      secondary: appText.secondary,
      disabled: appText.disabled
    },
    background: {
      default: cc.background,
      paper: cc.surface
    },
    divider: cc.outline,
    action: {
      // cc_design 全局交互叠加：hoverColor / selectedTileColor = primary @ 8% / 16%
      hover: cc.primaryAlpha8,
      selected: cc.primaryAlpha16,
      focus: cc.primaryAlpha8
    },
    // 深色模式下覆盖内置 grey 调色板，防止组件中的 grey.* 白块穿帮
    ...(isDark ? { grey: DarkGreyPalette } : {})
  },
  // 排版配置 (font-display / font-size-* / font-weight-* / line-height-*)
  typography: {
    fontFamily: '"Inter", "Noto Sans SC", sans-serif',
    fontSize: 16,
    h1: { fontSize: '36px', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: '28px', fontWeight: 600, lineHeight: 1.25 },
    h3: { fontSize: '22px', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '18px', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '16px', fontWeight: 600, lineHeight: 1.5 },
    h6: { fontSize: '14px', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '16px', fontWeight: 400, lineHeight: 1.6 },
    body2: { fontSize: '14px', fontWeight: 400, lineHeight: 1.6 },
    caption: { fontSize: '12px', fontWeight: 400, lineHeight: 1.5 },
    button: { fontWeight: 400 },
    overline: { fontSize: '12px', fontWeight: 400, lineHeight: 1.5 }
  },
  components: {
    MuiButton: {
      defaultProps: {
        size: 'small'
      },
      styleOverrides: {
        root: {
          borderRadius: '8px'
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        size: 'small'
      },
      styleOverrides: {
        root: {
          ...ComponentStyles.form.textField,
          // flutter InputDecoration hint = inputHint（onSurfaceVariant 同值）
          '& .MuiInputBase-input::placeholder': {
            color: cc.inputHint,
            opacity: 1
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          ...ComponentStyles.form.input,
          // flutter InputDecoration 描边 = outlineVariant（浅 #C4C4C4 / 深 #333333）
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: cc.outlineVariant
          }
        }
      }
    },
    // AppBar：浅色白底 + 品牌橙前景，深色 #1A1A1A 底 + 亮橙前景
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: cc.appBarBackground,
          color: cc.appBarForeground
        }
      }
    },
    // 抽屉/导航背景 = navBarBackground（浅白 / 深 #1A1A1A）
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: cc.navBarBackground
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          // 浮动层阴影（浅 #0A0F1A@25% / 深 黑@70%，对齐 cc_design floatingShadow）
          '& .MuiDialog-paper': {
            borderRadius: '12px',
            backgroundColor: cc.surface,
            boxShadow: floatingShadow
          }
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          backgroundColor: cc.surface,
          boxShadow: floatingShadow
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundColor: cc.surface
        }
      }
    },
    MuiAccordion: {
      defaultProps: {
        square: true
      },
      styleOverrides: {
        root: {
          borderRadius: '16px'
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          // flutter ListTile: selectedTileColor = primary @ 8%，文字/icon = primary
          '&.Mui-selected': {
            backgroundColor: cc.primaryAlpha8,
            color: cc.primary
          },
          '&:hover': {
            backgroundColor: cc.primaryAlpha8
          }
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          padding: BaseStyles.spacing.small,
          '&.Mui-selected': {
            backgroundColor: cc.primaryAlpha8,
            color: cc.primary
          },
          '&:hover': {
            backgroundColor: cc.primaryAlpha8
          }
        }
      }
    },
    // Chip（flutter ChipTheme: bg = surfaceVariant @ 60%，选中 = primary @ 16%）
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: isDark ? 'rgba(51, 51, 51, 0.6)' : 'rgba(242, 242, 242, 0.6)',
          '&.Mui-selected': {
            backgroundColor: cc.primaryAlpha16
          }
        }
      }
    },
    // 分隔线（flutter DividerTheme: outline @ 16%）
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: isDark ? 'rgba(92, 92, 92, 0.16)' : 'rgba(224, 224, 224, 0.16)'
        }
      }
    }
  }
  });
}

// 兼容旧引用：默认浅色主题
export const defaultTheme = createAppTheme('light');
